import React, { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/system";
import Stack from "@mui/material/Stack";
import EventsService from "../../services/EventsService";
import ReportsService from "../../services/ReportsService";
import { useStyles } from "../../events/styles/index.styles";
import { useTranslation } from "react-i18next";
import Typography from "@mui/material/Typography";
import { Button, Grid } from "@mui/material";
import IStationData from "../../types/Station";
import StationStudentsList from "./StationStudentsList";
import StationStudentsListV2 from "./StationStudentsListV2";
//import "moment/locale/pt-br";
import AvatarHiplade from "../../common/components/avatarhiplade";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import Switch from "@mui/material/Switch";
import palette from "../../common/styles/palette";
import TitleAndDivider from "./TitleAndDivider";
import { useObserver } from "../hooks/useObserver";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import { useNavigate } from "react-router-dom";
import { useModal } from "../../common/hooks/useModal";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import AddToDriveIcon from "@mui/icons-material/AddToDrive";
import CircularProgress from "@mui/material/CircularProgress";
import secureLocalStorage from "react-secure-storage";
import DriveBatchImportModal from "./drive-import/DriveBatchImportModal";

export default function EventStationsView({ event, status }: any) {
  if (!event) {
    return <Box />;
  }

  const styles = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down("xs"));
  const [stationList, setStationList] = React.useState<IStationData[]>([]);
  const [authAppraisersMap, setAuthAppraisersMap] = useState<Record<string, { inAppraisers: boolean; isMain: boolean }>>({});
  const [selectedAppraiserId, setSelectedAppraiserId] = useState<any>(null);
  const [meeting, setMeeting] = useState<boolean>(false);

  const navigate = useNavigate();
  // authenticated user id (stored as simple value)
  const authId = secureLocalStorage.getItem("id");
  const authRole = secureLocalStorage.getItem("role");

  const {
    getObserverStatusByStation,
    startObserver,
    stopObserver,
    observerStartState,
    observerStopState,
    observerStatusState,
  } = useObserver();

  const { handleOpenModal, dataModal, onResetModal } = useModal();

  const [checked, setChecked] = React.useState<any>("false");
  const [countToCallObserver, setCountToCallObserver] =
    React.useState<number>(0);
  const [loadingReport, setLoadingReport] = useState<string | null>(null);
  const [driveBatchModalOpen, setDriveBatchModalOpen] = useState(false);
  const [selectedStationForDrive, setSelectedStationForDrive] = useState<any>(null);
  const [refreshStudentsTrigger, setRefreshStudentsTrigger] = useState(0);

  const isAdmin = ["1", "admin", "2", "coordinator", "3", "teacher"].includes(String(authRole));

  const handleOpenDriveBatch = (station: any) => {
    setSelectedStationForDrive(station);
    setDriveBatchModalOpen(true);
  };

  const handleDriveBatchSuccess = () => {
    setRefreshStudentsTrigger((prev) => prev + 1);
  };

  const handleDownloadReport = async (stationId: any) => {
    setLoadingReport(String(stationId));
    try {
      const response = await ReportsService.getChecklistStationReport(stationId, event.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `relatorio_estacao_${stationId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // erro silencioso
    } finally {
      setLoadingReport(null);
    }
  };

  function stringToBoolean(value: string) {
    if (typeof value === "string") {
      const lowerCaseValue = value?.toLowerCase();
      return lowerCaseValue === "true"
        ? true
        : lowerCaseValue === "false"
        ? false
        : Boolean(value);
    }
    return value;
  }

  const getStationsByEvent = async (eventId) => {
    await EventsService.getStationsByEvent(eventId)
      .then((response: any) => {
        let stationsByEvent = response.data;
        //console.log("stationsByEvent: ", stationsByEvent);
        setStationList(stationsByEvent);
        try {
          // build a map of stationId -> whether authenticated user is in appraisers / is main appraiser
          const map: Record<string, { inAppraisers: boolean; isMain: boolean }> = {};
          Array.isArray(stationsByEvent) && stationsByEvent.forEach((st: any) => {
            const hasAppraisersArray = Array.isArray(st.appraisers_list) && st.appraisers_list.length > 0;
            const inAppraisers = hasAppraisersArray ? st.appraisers_list.some((a: any) => String(a.id) === String(authId)) : false;
            const isMain = String(st.appraiser_id) === String(authId);
            map[String(st.id)] = { inAppraisers, isMain };
          });
          setAuthAppraisersMap(map);
        } catch (e) {
          // ignore map building errors
        }
        try {
          // only run this validation for users with role '5'
          if (String(authRole) === "5" && authId) {
            const found = Array.isArray(stationsByEvent) && stationsByEvent.some((st: any) =>
              Array.isArray(st.appraisers_list) && st.appraisers_list.some((a: any) => String(a.id) === String(authId))
            );
            if (!found) {
              // navigate to MyEventsView for this event
              navigate(`/myevents/view/${eventId}`);
            }
          }
        } catch (e) {
          // ignore errors in this non-critical check
        }
      })
      .catch((e: Error) => {});
  };

  const openMeeting = (idStation: any) => {
    navigate("/events/meeting/" + event?.id + "/" + idStation);
  };

  const handleObserver = useCallback(async () => {
    getObserverStatusByStation(event.stations[0].id);
  }, [observerStatusState?.success]);

  const handleChangeChecked = useCallback(
    async (eventTarget: React.ChangeEvent<HTMLInputElement>) => {
      setCountToCallObserver(0);
      const targetChecked = eventTarget.target.checked;
      setChecked(targetChecked);

      if (!targetChecked) {
        await stopObserver({
          station_id: event.stations[0].id,
        });
        handleObserver();
      } else {
        await startObserver({
          event_id: event.id,
          station_id: event.stations[0].id,
        });
        handleObserver();
      }
    },
    [observerStatusState.data?.active, checked]
  );

  useEffect(() => {
    getStationsByEvent(event.id);
    // handleObserver();
    // setCountToCallObserver(1);
  }, []);

  // Always scroll page to top when this view is loaded
  useEffect(() => {
    try {
      if (typeof window !== "undefined" && window.scrollTo) {
        window.scrollTo(0, 0);
      }
    } catch (e) {
      // ignore in non-browser environments
    }
  }, []);

  useEffect(() => {
    if (countToCallObserver === 1 && !observerStatusState.loading) {
      const state = observerStatusState.data?.active;
      setChecked(state);
    }
  }, [
    countToCallObserver,
    observerStatusState.success,
    observerStatusState.data?.active,
    checked,
    observerStatusState.loading,
  ]);

  return (
    <Box className={styles.container}>
      <TitleAndDivider title={t("title_page_form_station_management")} />

      {status !== "concluded" && (
        <Stack
          className={styles.eventBoxListMonitoringRoomWithoutBG}
          sx={{ mt: 4 , display: secureLocalStorage.getItem("role") == 5 ? "none" : "flex"}}
          direction="row"
          alignItems="center"
          spacing={1}
          flexWrap={{ xs: "wrap", lg: "nowrap" }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              mr: 1,
              mb: { xs: 0, lg: 0 },
            }}
          >
            <SwitchAccountIcon sx={{ color: palette.brand, fontSize: 20 }} />
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              justifyContent: "flex-start",
            }}
          >
            <Typography
              variant="body2"
              className={styles.titleStudent}
              sx={{ textAlign: "left", "& strong": { fontWeight: 600 } }}
            >
              <strong>{t("title_strong_event_stations_view")}</strong>
              {t("description_dialog_event_stations_view")}
            </Typography>
          </Box>
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              ml: { xs: "auto", lg: "auto" },
              mt: { xs: 1, sm: 0 },
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "flex-end", lg: "flex-end" },
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ mr: 1, color: palette.textMuted }}
            >
              {observerStartState.loading
                ? "carregando o status..."
                : observerStatusState.data?.status}
            </Typography>
            <Switch
              checked={stringToBoolean(checked)}
              onChange={handleChangeChecked}
              inputProps={{ "aria-label": "controlled" }}
              color="success"
            />
          </Stack>
        </Stack>
      )}

      {stationList?.map((station, index) => {
        //console.log("station row: ", station);
        // if role is 5, validate if authenticated user is an appraiser for this station
        let isAuthInAppraisers = false;
        let isAuthMainAppraiser = false;
        if (authAppraisersMap && authAppraisersMap[String(station.id)]) {
          isAuthInAppraisers = authAppraisersMap[String(station.id)].inAppraisers;
          isAuthMainAppraiser = authAppraisersMap[String(station.id)].isMain;
        } else {
          const hasAppraisersArray = Array.isArray(station.appraisers_list) && station.appraisers_list.length > 0;
          isAuthInAppraisers = hasAppraisersArray
            ? station.appraisers_list.some((a: any) => String(a.id) === String(authId))
            : false;
          isAuthMainAppraiser = String(station.appraiser_id) === String(authId);
        }

        if (String(authRole) === "5") {
          // if authenticated user is not an appraiser on this station, hide the box
          if (!isAuthInAppraisers && !isAuthMainAppraiser) {
            return null;
          }
        }

        return (
        <Box key={station.id} sx={{ mt: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={isAdmin ? 12 : 12} md={isAdmin ? 6 : 12} lg={isAdmin ? 7 : 12}>
              <Typography
                className={styles.eventStationTitle}
                display="inline-block"
              >
                {station.name + " / "}
              </Typography>

              <Typography
                className={styles.eventStationSubTitle}
                display="inline-block"
              >
                {station?.theme_name}
              </Typography>

              <Typography
                className={styles.eventStationRoom}
                display="inline-block"
              >
                / {station?.room_name}
              </Typography>
            </Grid>
            {isAdmin && (
              <Grid
                item
                xs={12}
                md={6}
                lg={5}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-start", md: "flex-end" },
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddToDriveIcon />}
                  sx={{
                    color: palette.brand,
                    borderColor: palette.brand,
                    "&:hover": {
                      borderColor: palette.brandDark,
                      bgcolor: "rgba(106, 48, 222, 0.04)",
                    },
                  }}
                  onClick={() => handleOpenDriveBatch(station)}
                >
                  Importar do Drive
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  disabled={loadingReport === String(station.id)}
                  startIcon={
                    loadingReport === String(station.id) ? (
                      <CircularProgress size={14} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  onClick={() => handleDownloadReport(station.id)}
                >
                  Relatório Checklist
                </Button>
              </Grid>
            )}
          </Grid>

          <Grid
            item
            xs={2}
            mb={2}
            className={styles.boxTitleStation}
            sx={{ justifyContent: "flex-start" }}
          >
            {/* Lista o nome dos avaliadores vinculados a estação */}
            <Stack
              sx={{
                flexDirection: "row",
                alignItems: "center",
                gap: 3,
                "& .MuiAvatar-root": {
                  mr: 1,
                  fontWeight: 700,
                  fontSize: "0.625rem",
                },
              }}
            >
              {Array.isArray(station.appraisers_list) && station.appraisers_list.length > 0 ? (
                station.appraisers_list.map((appraiser: any) => (
                  <Stack
                    key={appraiser.id}
                    sx={{
                      flexDirection: "row",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      // permite toggle do filtro apenas para roles permitidas (admin/teacher ou códigos numéricos 1/2)
                      const allowed = ["admin", "teacher", "student", "1", "2", "3"];
                      if (allowed.includes(String(authRole))) {
                        setSelectedAppraiserId((prev) => (prev === appraiser.id ? null : appraiser.id));
                      }
                      // else {
                      //   handleOpenModal({ displayName: "AppraisersList" });
                      // }
                    }}
                  >
                    <AvatarHiplade
                      nameUser={appraiser.name}
                      id={appraiser.id}
                      size="mini"
                      only
                    />
                    <Box className={styles.appraiserName}>
                      {appraiser.name}{" "}
                      <Box component="span" className={styles.appraiserRole}>
                        ({appraiser.role_string})
                      </Box>
                      {selectedAppraiserId === appraiser.id ? " • (filtrado)" : ""}
                    </Box>
                  </Stack>
                ))
              ) : (
                <Stack
                  sx={{ flexDirection: "row", alignItems: "center", cursor: "pointer" }}
                  onClick={() => {
                    const allowed = ["admin", "teacher", "1", "2"];
                    if (allowed.includes(String(authRole))) {
                      setSelectedAppraiserId((prev) => (prev === station.appraiser_id ? null : station.appraiser_id));
                    } else {
                      handleOpenModal({ displayName: "AppraisersList" });
                    }
                  }}
                >
                  <AvatarHiplade
                    nameUser={station.appraiser_name}
                    id={station.appraiser_id}
                    size="mini"
                    only
                  />
                  <Box className={styles.appraiserName}>
                    {station.appraiser_name}
                    {selectedAppraiserId === station.appraiser_id ? " • (filtrado)" : ""}
                  </Box>
                </Stack>
              )}
            </Stack>
            {/* Fim da lista o nome dos avaliadores vinculados a estação */}
          </Grid>

              {/* Sala de meeting */}
          {status !== "concluded" && meeting == true && (
            <Box>
              <Grid
                className={styles.eventBoxListMeeting}
                sx={{ mt: 4 }}
                container
                direction="row"
                spacing={2}
                alignItems="center"
                flexWrap={{ xs: "wrap", lg: "nowrap" }}
              >
                <Grid
                  item
                  xs="auto"
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    mb: { xs: 0, lg: 0 },
                    mr: { xs: 1, lg: 0 },
                  }}
                >
                  <ContactPhoneOutlinedIcon
                    sx={{ color: palette.brand, fontSize: 20 }}
                  />
                </Grid>
                <Grid item xs>
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: "left",
                      "& strong": { fontWeight: 600 },
                    }}
                    className={styles.titleMetting}
                  >
                    <strong>
                      {t("title_strong_event_stations_view_meeting")}
                    </strong>
                    {t("description_dialog_event_stations_view_meeting")}{" "}
                    <strong>
                      {t("second_title_strong_event_stations_view_meeting")}
                    </strong>
                    {t("second_description_dialog_event_stations_view_meeting")}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm="auto"
                  sx={{
                    textAlign: { xs: "right", lg: "right" },
                    mt: { xs: 2, lg: 0 },
                    ml: { xs: "auto", lg: 0 },
                    display: "flex",
                    justifyContent: { xs: "flex-end", lg: "flex-end" },
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  <Button
                    variant="outlined"
                    sx={{ fontSize: "0.75rem", ml: { lg: 2 } }}
                    className={styles.buttonMeeting}
                    onClick={() => openMeeting(station?.id)}
                  >
                    {t("btn_enter")}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {isMobile && (
            <>
              <Typography
                variant="body2"
                sx={{
                  mb: 2,
                  textAlign: "center",
                  fontWeight: 500,
                }}
              >
                Clique nos nomes abaixo para acessar as simulações, os icones a
                seguir correspondem o status de correção:
              </Typography>

              <Stack
                direction={isExtraSmall ? "column" : "row"}
                spacing={isExtraSmall ? 1 : 2}
                justifyContent="center"
                alignItems={isExtraSmall ? "flex-start" : "center"}
                sx={{
                  mb: 2,
                  width: "100%",
                  ...(isExtraSmall && { pl: 2 }),
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CheckCircleIcon sx={{ color: palette.emeraldDeep, mr: 0.5 }} />
                  <Typography variant="body2">Corrigido</Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <AccessTimeFilledIcon sx={{ color: palette.warning, mr: 0.5 }} />
                  <Typography variant="body2">Em correção</Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <ErrorOutlinedIcon sx={{ color: palette.danger, mr: 0.5 }} />
                  <Typography variant="body2">Não corrigido</Typography>
                </Box>
              </Stack>
            </>
          )}

          {/* <StationStudentsList
            studentsgroupId={event.studentsgroup_id}
            eventId={event.id}
            stationId={station.id}
            status={status}
            station={station}
            appraiserId={selectedAppraiserId}
            isAuthInAppraisers={isAuthInAppraisers}
          /> */}


          <StationStudentsListV2
            studentsgroupId={event.studentsgroup_id}
            eventId={event.id}
            stationId={station.id}
            status_m={status}
            station={station}
            appraiserId={selectedAppraiserId}
            isAuthInAppraisers={isAuthInAppraisers}
            refreshTrigger={refreshStudentsTrigger}
            isLazy={index >= 3}
          />
        </Box>
      );
      })}

      {selectedStationForDrive && (
        <DriveBatchImportModal
          open={driveBatchModalOpen}
          onClose={() => {
            setDriveBatchModalOpen(false);
            setSelectedStationForDrive(null);
          }}
          onSuccess={handleDriveBatchSuccess}
          eventId={event.id}
          eventName={event.name}
          stationId={selectedStationForDrive.id}
          stationName={selectedStationForDrive.name}
        />
      )}
    </Box>
  );
}
